import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const COST_CATEGORIES = ["SUBCONTRACT", "MATERIAL", "LABOUR", "OTHER"];
const EMPTY_LINE = { wbs_item_id: "", description: "", uom: "", quantity: "", rate: "", category: "OTHER" };

export default function Estimates() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [estimates, setEstimates] = useState([]);
  const [wbsItems, setWbsItems] = useState([]);
  const [bva, setBva] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: est }, { data: wbs }, { data: rows }] = await Promise.all([
        api.get(`/projects/${projectId}/estimates`),
        api.get(`/projects/${projectId}/wbs`, { params: { include_groups: false } }),
        api.get(`/projects/${projectId}/budget-vs-actual`),
      ]);
      setEstimates(est);
      setWbsItems(wbs);
      setBva(rows);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const itemLabel = (id) => {
    const item = wbsItems.find((w) => w.id === id);
    return item ? `${item.code} — ${item.name}` : "—";
  };

  const approve = async (estimate) => {
    setBusyId(estimate.id);
    try {
      await api.post(`/projects/${projectId}/estimates/${estimate.id}/approve`);
      toast.success(`Version ${estimate.version} approved`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="estimates-page">
      <PageHeader
        title="Estimates & Budgets"
        description="Version-controlled cost estimates by WBS item. Approving a version sets the budget used across Cost Entries and Project 360."
        action={isProjectManager && <CreateEstimateDialog projectId={projectId} wbsItems={wbsItems} onCreated={load} />}
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && estimates.length === 0 ? (
          <EmptyState title="No estimates yet" description="Create the first version to set a project budget." testId="estimates-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead>Lines</TableHead><TableHead className="text-right">Total amount</TableHead><TableHead>Remarks</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {estimates.map((e) => (
                <TableRow key={e.id} data-testid={`estimate-row-${e.version}`}>
                  <TableCell className="font-mono text-xs font-semibold text-foreground">v{e.version}</TableCell>
                  <TableCell><StatusPill value={e.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.lines.length}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtNum(e.total_amount, 2)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{e.remarks || "—"}</TableCell>
                  <TableCell className="text-right">
                    {isProjectManager && e.status === "DRAFT" && (
                      <Button size="sm" variant="outline" disabled={busyId === e.id} onClick={() => approve(e)} data-testid={`approve-estimate-button-${e.version}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card">
        <div className="border-b border-border p-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Budget vs Actual by WBS</h2>
        </div>
        {bva.length === 0 ? (
          <EmptyState title="No budget rows yet" description="Approve an estimate to see budget vs actual by activity." testId="bva-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead className="text-right">Budget</TableHead><TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Committed</TableHead><TableHead className="text-right">Variance</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {bva.map((row, idx) => (
                <TableRow key={row.wbs_item_id || idx} data-testid={`bva-row-${row.wbs_item_id || idx}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{row.code || "—"}</TableCell>
                  <TableCell className="text-foreground">{row.name || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(row.budget, 2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(row.actual, 2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmtNum(row.committed, 2)}</TableCell>
                  <TableCell className={`text-right font-mono text-xs font-semibold ${Number(row.variance) < 0 ? "text-rose-400" : "text-emerald-400"}`}>{fmtNum(row.variance, 2)}</TableCell>
                  <TableCell>{row.over_budget && <span className="rounded-md border border-rose-500/30 bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400" data-testid={`over-budget-flag-${row.wbs_item_id || idx}`}>Over budget</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateEstimateDialog({ projectId, wbsItems, onCreated }) {
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx, field, value) => setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/estimates`, {
        remarks: remarks || null,
        lines: lines.map((l) => ({ ...l, wbs_item_id: l.wbs_item_id || null, quantity: l.quantity || "0", rate: l.rate || "0" })),
      });
      toast.success("Estimate version created");
      setRemarks("");
      setLines([{ ...EMPTY_LINE }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-estimate-button"><Plus className="h-4 w-4" /> New Version</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader><DialogTitle>New Estimate Version</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-estimate-form">
          <div>
            <Label>Lines</Label>
            <div className="mt-1.5 space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="grid grid-cols-12 items-end gap-2 rounded-lg border border-border p-2.5" data-testid={`estimate-line-row-${idx}`}>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Activity</Label>
                    <Select value={l.wbs_item_id || "__none__"} onValueChange={(v) => updateLine(idx, "wbs_item_id", v === "__none__" ? "" : v)}>
                      <SelectTrigger className="mt-1" data-testid={`estimate-line-wbs-select-${idx}`}><SelectValue placeholder="Not linked" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not linked</SelectItem>
                        {wbsItems.map((w) => <SelectItem key={w.id} value={w.id}>{w.code} — {w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[11px]">Description</Label>
                    <Input required className="mt-1" value={l.description} onChange={(e) => updateLine(idx, "description", e.target.value)} data-testid={`estimate-line-description-input-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[11px]">Category</Label>
                    <Select value={l.category} onValueChange={(v) => updateLine(idx, "category", v)}>
                      <SelectTrigger className="mt-1" data-testid={`estimate-line-category-select-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{COST_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-1">
                    <Label className="text-[11px]">Qty</Label>
                    <Input type="number" className="mt-1" value={l.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} data-testid={`estimate-line-quantity-input-${idx}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[11px]">Rate</Label>
                    <Input type="number" className="mt-1" value={l.rate} onChange={(e) => updateLine(idx, "rate", e.target.value)} data-testid={`estimate-line-rate-input-${idx}`} />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    {lines.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(idx)} data-testid={`remove-estimate-line-button-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addLine} data-testid="add-estimate-line-button">
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5" data-testid="estimate-remarks-input" /></div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-estimate-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
